import * as O from "fp-ts/Option"
import * as E from "fp-ts/lib/Either"
import { Calendar } from "../../lib/calendar"
import * as FirebaseAuth from "firebase/auth"
import { PageStatus } from "../../lib/types"
import { pipe } from "fp-ts/lib/function"
import dayjs from "dayjs"
import * as A from "fp-ts/Array"
import { AgentViewing, Variant } from "@flypengine/myFlyp-lib"
import { Types } from "@flypengine/lib-shared"
import * as Http from "../../Http"

export type Config<msg> = {
  user: FirebaseAuth.User
  propcode: string
  wrapperMsg: (m: Msg) => msg
  dealViewingsMsg: () => msg
}

export type ViewingsCalendar = {
  entries: AgentViewing[]
  selectedDay: O.Option<{ date: string; entries: AgentViewing[] }>
  upcoming: O.Option<AgentViewing>
}

export type CalendarSetup = {
  calendarAvailability: O.Option<Types.CalendarAvailability>
  newCalendarAvailability: O.Option<Types.CalendarAvailability>
  submitAvailability: Http.Result<{}>
  calendarNoticePeriodHours: O.Option<number>
  savedCalendarOverride: Types.CalendarOverride
  calendarOverride: Types.CalendarOverride
  calendarPaused: boolean
}

export type CancellationRequest = {
  viewing: AgentViewing
  reason: string
  submitStatus: Http.Result<{}>
}

export type Model = {
  calendar: O.Option<Calendar>
  calendarModel: E.Either<PageStatus, ViewingsCalendar>
  calendarSetup: CalendarSetup
  isCalendarSetupOpen: boolean
  cancellationRequest: O.Option<CancellationRequest>
}

export type SetCalendarAvailabilityField = {
  [key: string]: O.Option<Types.CalendarStartEndTimes>
}

export const _init = "init"
export const _calendarContents = "calendarContents"
export const _calendarDayModel = "calendarDayModel"
export const _viewingsCalendarReq = "viewingsCalendarReq"
export const _viewingsCalendarRes = "viewingsCalendarRes"
export const _toggleCalendarSetup = "toggleCalendarSetup"
export const _onChangeCalendarAvailability = "onChangeCalendarAvailability"
export const _addAvailabilityTimeSlot = "addAvailabilityTimeSlot"
export const _removeAvailabilityTimeSlot = "removeAvailabilityTimeSlot"
export const _onChangeMinimumNotice = "onChangeMinimumNotice"
export const _updateCalendarSettingsReq = "updateCalendarSettingsReq"
export const _updateCalendarSettingsRes = "updateCalendarSettingsRes"
export const _openCancellationModal = "openCancellationModal"
export const _closeCancellationModal = "closeCancellationModal"
export const _changeCancellationReason = "changeCancellationReason"
export const _submitCancellationReq = "submitCancellationReq"
export const _submitCancellationRes = "submitCancellationRes"
export const _onChangeAvailabilityOverrideDates =
  "onChangeAvailabilityOverrideDates"
export const _onAddAvailabilityOverrideTimeSlot =
  "onAddAvailabilityOverrideTimeSlot"
export const _onChangeAvailabilityOverrideTimeSlot =
  "onChangeAvailabilityOverrideTimeSlot"
export const _onAddAvailabilityOverride = "onAddAvailabilityOverride"
export const _onRemoveAvailabilityOverride = "onRemoveAvailabilityOverride"

export type Msg =
  | Variant.Tagged<typeof _init, void>
  | Variant.Tagged<typeof _calendarContents, Calendar>
  | Variant.Tagged<typeof _calendarDayModel, O.Option<string>>
  | Variant.Tagged<typeof _viewingsCalendarReq, void>
  | Variant.Tagged<
      typeof _viewingsCalendarRes,
      Http.Result<Record<string, AgentViewing[]>>
    >
  | Variant.Tagged<typeof _toggleCalendarSetup, void>
  | Variant.Tagged<
      typeof _onChangeCalendarAvailability,
      { setField: SetCalendarAvailabilityField; idx: number }
    >
  | Variant.Tagged<
      typeof _addAvailabilityTimeSlot,
      keyof Types.CalendarAvailability
    >
  | Variant.Tagged<
      typeof _removeAvailabilityTimeSlot,
      keyof Types.CalendarAvailability
    >
  | Variant.Tagged<typeof _onChangeMinimumNotice, number>
  | Variant.Tagged<typeof _updateCalendarSettingsReq, void>
  | Variant.Tagged<typeof _updateCalendarSettingsRes, Http.Result<{}>>
  | Variant.Tagged<typeof _openCancellationModal, AgentViewing>
  | Variant.Tagged<typeof _closeCancellationModal, void>
  | Variant.Tagged<typeof _changeCancellationReason, string>
  | Variant.Tagged<typeof _submitCancellationReq, void>
  | Variant.Tagged<typeof _submitCancellationRes, Http.Result<{}>>
  | Variant.Tagged<
      typeof _onChangeAvailabilityOverrideDates,
      {
        blockedDates: O.Option<Types.CalendarOverrideDatesTimes["dates"]>
        idx: number
      }
    >
  | Variant.Tagged<
      typeof _onChangeAvailabilityOverrideTimeSlot,
      {
        timeSlot: O.Option<Types.CalendarStartEndTimes>
        outerIdx: number
        innerIdx: number
      }
    >
  | Variant.Tagged<
      typeof _onAddAvailabilityOverrideTimeSlot,
      { idx: number; timeSlot: O.Option<Types.CalendarStartEndTimes> }
    >
  | Variant.Tagged<typeof _onAddAvailabilityOverride, void>
  | Variant.Tagged<typeof _onRemoveAvailabilityOverride, number>
export const Msg = Variant.module<Msg>({
  init: value => Variant.tagged(_init, value),
  calendarContents: value => Variant.tagged(_calendarContents, value),
  calendarDayModel: value => Variant.tagged(_calendarDayModel, value),
  viewingsCalendarReq: value => Variant.tagged(_viewingsCalendarReq, value),
  viewingsCalendarRes: value => Variant.tagged(_viewingsCalendarRes, value),
  toggleCalendarSetup: value => Variant.tagged(_toggleCalendarSetup, value),
  onChangeCalendarAvailability: value =>
    Variant.tagged(_onChangeCalendarAvailability, value),
  addAvailabilityTimeSlot: value =>
    Variant.tagged(_addAvailabilityTimeSlot, value),
  removeAvailabilityTimeSlot: value =>
    Variant.tagged(_removeAvailabilityTimeSlot, value),
  onChangeMinimumNotice: value => Variant.tagged(_onChangeMinimumNotice, value),
  updateCalendarSettingsReq: value =>
    Variant.tagged(_updateCalendarSettingsReq, value),
  updateCalendarSettingsRes: value =>
    Variant.tagged(_updateCalendarSettingsRes, value),
  openCancellationModal: value => Variant.tagged(_openCancellationModal, value),
  closeCancellationModal: value =>
    Variant.tagged(_closeCancellationModal, value),
  changeCancellationReason: value =>
    Variant.tagged(_changeCancellationReason, value),
  submitCancellationReq: value => Variant.tagged(_submitCancellationReq, value),
  submitCancellationRes: value => Variant.tagged(_submitCancellationRes, value),
  onChangeAvailabilityOverrideDates: value =>
    Variant.tagged(_onChangeAvailabilityOverrideDates, value),
  onAddAvailabilityOverrideTimeSlot: value =>
    Variant.tagged(_onAddAvailabilityOverrideTimeSlot, value),
  onChangeAvailabilityOverrideTimeSlot: value =>
    Variant.tagged(_onChangeAvailabilityOverrideTimeSlot, value),
  onAddAvailabilityOverride: value =>
    Variant.tagged(_onAddAvailabilityOverride, value),
  onRemoveAvailabilityOverride: value =>
    Variant.tagged(_onRemoveAvailabilityOverride, value),
})

export const viewingsInitModel: Model = {
  calendar: O.none,
  calendarModel: E.left("Not Attempted"),
  calendarSetup: {
    calendarAvailability: O.none,
    newCalendarAvailability: O.some(Types.defaultCalendarAvailability),
    submitAvailability: E.left(Http.ReqStatus.notAttempted()),
    calendarNoticePeriodHours: O.none,
    savedCalendarOverride: O.none,
    calendarOverride: O.none,
    calendarPaused: false,
  },
  isCalendarSetupOpen: false,
  cancellationRequest: O.none,
}

export const matchedViewings = (entries: AgentViewing[], dayStr: string) =>
  pipe(
    entries,
    A.filter(entry => dayjs(entry.viewingDate).format("YYYY-MM-DD") === dayStr)
  )

export const initViewingsCalendarModel = (
  data: E.Either<PageStatus, Omit<ViewingsCalendar, "selectedDay">>
): E.Either<PageStatus, ViewingsCalendar> =>
  pipe(
    data,
    E.map(calendarModel => ({ ...calendarModel, selectedDay: O.none }))
  )
